import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Loader2, Trash2, ExternalLink, ImageOff, ShieldAlert } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader } from './AdminUI';

const TYPE_LABEL = {
  city: 'Città',
  activity: 'Attività',
  category: 'Categoria',
  subcategory: 'Sottocategoria',
  banner: 'Banner',
};

const TYPE_LINK = {
  city: '/admin/cities',
  activity: '/admin/activities',
  category: '/admin/categories',
  subcategory: '/admin/subcategories',
  banner: '/admin/banners',
};

export default function AdminBrokenImages() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = () => {
    setLoading(true);
    adminApi.brokenImages()
      .then(r => { setItems(r.items || []); setTotal(r.total || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doClear = async () => {
    if (!window.confirm(`Rimuovere TUTTI i ${total} riferimenti a foto rotte dal database?\n\nI record verranno mantenuti, solo l'URL della foto verrà svuotato — così potrai ricaricarla senza dover eliminare tutto.`)) return;
    setClearing(true);
    try {
      const r = await adminApi.clearBrokenImages();
      alert(`✅ Ripulite ${r.cleared} righe. Ricarica per verificare.`);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setClearing(false); }
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);

  const groupedByCity = filtered.reduce((acc, i) => {
    const k = i.city || '—';
    (acc[k] = acc[k] || []).push(i);
    return acc;
  }, {});

  return (
    <div data-testid="admin-broken-images">
      <PageHeader
        title="Foto rotte da riparare"
        subtitle={`${total} riferimenti a immagini locali persi (deploy Railway effimero)`}
        action={
          total > 0 && (
            <button onClick={doClear} disabled={clearing}
                    data-testid="clear-broken-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60">
              {clearing ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
              Pulisci tutti
            </button>
          )
        }
      />

      <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm">
        <ShieldAlert size={20} className="mt-0.5 flex-shrink-0"/>
        <div>
          <p className="font-semibold">Cosa sono le foto rotte</p>
          <p className="text-xs mt-1 text-amber-800/90">
            Sono foto caricate su Railway prima dell'introduzione dell'Object Storage.
            Railway azzera il filesystem ad ogni redeploy, quindi ogni URL <code className="bg-white px-1 rounded">/api/uploads/*</code> è ormai un file mancante.
            Puoi (a) andare voce per voce e ricaricare la foto, oppure (b) usare "Pulisci tutti" per svuotare gli URL rotti in blocco così le nuove foto si potranno caricare senza confusione.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-white text-xs">
          {['all', 'activity', 'city', 'category', 'subcategory', 'banner'].map(k => (
            <button key={k} onClick={() => setFilter(k)}
                    data-testid={`broken-filter-${k}`}
                    className={`px-3 py-1.5 font-semibold ${filter === k ? 'bg-slate-800 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k === 'all' ? `Tutti (${items.length})` : `${TYPE_LABEL[k]} (${items.filter(i => i.type === k).length})`}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="broken-refresh-btn"
                className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50">
          <RefreshCw size={13}/> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-slate-500">
          <Loader2 size={20} className="animate-spin mr-2"/> Scansione database...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center rounded-lg border border-dashed border-emerald-200 bg-emerald-50">
          <ImageOff size={36} className="mx-auto text-emerald-500 mb-2"/>
          <p className="text-sm text-emerald-800 font-semibold">Nessuna foto rotta 🎉</p>
          <p className="text-xs text-emerald-700 mt-1">Tutte le immagini nel DB sono valide (URL esterni o object storage).</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByCity).map(([city, rows]) => (
            <div key={city} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                {city} <span className="text-xs text-slate-500 font-normal ml-1">({rows.length})</span>
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map((r, idx) => (
                  <div key={`${r.entity_id}-${r.field}-${idx}`}
                       data-testid={`broken-row-${idx}`}
                       className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                    <div className="w-14 h-14 flex-shrink-0 rounded bg-red-50 border border-red-200 flex items-center justify-center">
                      <AlertTriangle size={20} className="text-red-500"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-200 text-slate-700">
                          {TYPE_LABEL[r.type] || r.type}
                        </span>
                        <span className="font-semibold text-slate-800 truncate">{r.name}</span>
                        <span className="text-[10px] text-slate-400 px-1.5 py-0.5 bg-slate-100 rounded">campo: {r.field}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-1 font-mono">{r.image}</div>
                    </div>
                    {TYPE_LINK[r.type] && (
                      <Link to={TYPE_LINK[r.type]}
                            data-testid={`broken-edit-${idx}`}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0">
                        Ripara <ExternalLink size={12}/>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
